import { useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';

import { useCatalogMediaAssets, useDeleteAsset, useUploadAsset } from '../../hooks';
import { MEDIA_OWNER_TYPE } from '../../model/options';
import type { ToastSeverity } from '../../lib/useToast';

interface MediaManagerProps {
  /** The owning product id (omitted for a not-yet-saved product). */
  ownerId?: string;
  /** Current image URLs (the product's gallery). */
  images: string[];
  onChange: (images: string[]) => void;
  disabled?: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

const TILE = 96;

/**
 * Product photo gallery — uploads to web-media-service (via the BFF) and appends the returned
 * stable public URL to the product's images.
 *
 * Removing a tile: a photo uploaded through this component is "media-managed" — web-catalog-master-
 * service's own upsert deliberately KEEPS media-managed gallery entries no matter what `images` is
 * resubmitted with (it only replaces pass-through/external URLs — see CatalogMasterService
 * .replacePassThroughImages's javadoc), so filtering the URL out of the local array and saving the
 * product does NOT detach it. The only thing that does is deleting the underlying asset in
 * web-media-service (it publishes media.asset.deleted, which the master's MediaAssetListener
 * consumes to actually remove the reference — bug fix 2026-08-27, see docs/adr for the incident this
 * closes). So: look the tile's URL up in this owner's asset list and delete the real asset first;
 * only fall back to a local-only removal for a URL that isn't a known media-managed asset (a legacy
 * pass-through entry, or the asset list hasn't loaded yet).
 */
export function MediaManager({ ownerId, images, onChange, disabled, onResult }: MediaManagerProps) {
  const { t } = useTranslation('catalog');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const upload = useUploadAsset();
  const deleteAsset = useDeleteAsset();
  const [removingUrls, setRemovingUrls] = useState<ReadonlySet<string>>(new Set());

  // Deleting an asset is async; if the user removes a second tile before the first delete settles,
  // a callback closed over a stale `images`/index from an earlier render would compute the wrong
  // result. Route every removal through this ref so it always filters the LATEST array.
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const assets = useCatalogMediaAssets(
    { ownerType: MEDIA_OWNER_TYPE, ownerId },
    { enabled: Boolean(ownerId) },
  );
  const assetIdByUrl = useMemo(() => {
    const map = new Map<string, string>();
    for (const asset of assets.data ?? []) {
      map.set(asset.publicUrl, asset.assetId);
    }
    return map;
  }, [assets.data]);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    upload.mutate(
      { file, ownerType: MEDIA_OWNER_TYPE, ownerId },
      {
        onSuccess: (asset) => {
          onChange([...images, asset.publicUrl]);
          onResult('success', t('mediaManager.uploaded'));
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  // Filters by URL value against the CURRENT array (via imagesRef), not the index/array captured
  // when the removal was kicked off — safe even if another tile finished removing in the meantime.
  const removeLocally = (url: string) => onChange(imagesRef.current.filter((u) => u !== url));

  const removeAt = (index: number) => {
    const url = images[index];
    if (removingUrls.has(url)) return;
    const assetId = assetIdByUrl.get(url);
    if (!assetId) {
      // Not a known media-managed asset (legacy pass-through URL, or the asset list is still
      // loading) — nothing to delete server-side, so just drop it from the draft as before.
      removeLocally(url);
      return;
    }
    setRemovingUrls((prev) => new Set(prev).add(url));
    deleteAsset.mutate(assetId, {
      onSuccess: () => {
        removeLocally(url);
        onResult('success', t('mediaManager.removed'));
      },
      onError: (error) => onResult('error', error.message),
      onSettled: () => {
        setRemovingUrls((prev) => {
          const next = new Set(prev);
          next.delete(url);
          return next;
        });
      },
    });
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
        {images.map((url, index) => {
          const removing = removingUrls.has(url);
          return (
            <Box
              key={`${url}-${index}`}
              sx={{
                position: 'relative',
                width: TILE,
                height: TILE,
                borderRadius: 2,
                overflow: 'hidden',
                border: (t) => `1px solid ${t.palette.divider}`,
              }}
            >
              <Box
                component="img"
                src={url}
                alt={t('mediaManager.photoAlt', { index: index + 1 })}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                  opacity: removing ? 0.5 : 1,
                }}
              />
              {!disabled ? (
                <IconButton
                  size="small"
                  aria-label={t('mediaManager.removeAria', { index: index + 1 })}
                  onClick={() => removeAt(index)}
                  disabled={removing}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                  }}
                >
                  {removing ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : (
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              ) : null}
            </Box>
          );
        })}

        {!disabled ? (
          <Box
            component="button"
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            sx={{
              width: TILE,
              height: TILE,
              borderRadius: 2,
              border: (t) => `1px dashed ${t.palette.divider}`,
              bgcolor: 'action.hover',
              cursor: upload.isPending ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            {upload.isPending ? (
              <CircularProgress size={20} />
            ) : (
              <>
                <AddPhotoAlternateRoundedIcon fontSize="small" />
                <Typography variant="caption">{t('mediaManager.upload')}</Typography>
              </>
            )}
          </Box>
        ) : null}
      </Stack>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {images.length === 0 && disabled ? (
        <Typography variant="caption" color="text.secondary">
          {t('mediaManager.noPhotos')}
        </Typography>
      ) : null}
    </Stack>
  );
}
